import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Phone,
  Code,
  Boxes,
  Cpu,
  Layers,
  Link as LinkIcon,
  ShieldCheck,
  Rocket,
  Workflow,
  Sparkles,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CTABanner from '../components/CTABanner';
import SEO from '../components/SEO';
import TechBackground from '../components/TechBackground';
import { companyInfo, services } from '../data/mock';

const capabilities = [
  {
    icon: Workflow,
    title: 'Custom Business Software',
    desc: 'Tailored internal tools, dashboards and ERPs that replace spreadsheets and automate the repetitive work slowing your team down.',
  },
  {
    icon: Boxes,
    title: 'Blockchain & Web3',
    desc: 'Smart contracts, tokenization and blockchain-backed platforms, including real estate fractional investment systems that make ownership accessible to more people.',
  },
  {
    icon: Layers,
    title: 'SaaS Platforms',
    desc: 'Multi-tenant SaaS products engineered for scale, with billing, roles, analytics and the infrastructure to grow from MVP to market leader.',
  },
  {
    icon: LinkIcon,
    title: 'APIs & Integrations',
    desc: 'Connect your stack. Stripe, HubSpot, Salesforce, AI models, payment rails, CRMs. We stitch the moving parts into one clean system.',
  },
  {
    icon: Cpu,
    title: 'AI-Powered Features',
    desc: 'Embed GPT, vision, voice and automation into your product so it works smarter, not just harder, for every user.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure & Production-Ready',
    desc: 'Enterprise-grade security, tested pipelines, observability and documentation from day one. Built for production, not prototypes.',
  },
];

const process = [
  {
    step: '01',
    title: 'Discover',
    desc: 'We sit down with you to map workflows, uncover operational bottlenecks and define what success looks like.',
  },
  {
    step: '02',
    title: 'Design',
    desc: 'System architecture, data models and UX flows designed for clarity, scale and a great experience for your users.',
  },
  {
    step: '03',
    title: 'Develop',
    desc: 'Production-grade engineering with clean code, automated tests and CI/CD, shipped in focused sprints you can see.',
  },
  {
    step: '04',
    title: 'Refine',
    desc: 'Iterate with real users, tighten edge cases, optimize performance and polish the details that separate good from great.',
  },
  {
    step: '05',
    title: 'Deliver',
    desc: 'Launch, hand over documentation, train your team and stay on for support as your product and business grow.',
  },
];

const stack = [
  'React', 'Next.js', 'Node.js', 'Python', 'FastAPI', 'TypeScript',
  'Solidity', 'Ethereum', 'Polygon', 'PostgreSQL', 'MongoDB', 'AWS',
  'GCP', 'Docker', 'Kubernetes', 'Stripe', 'OpenAI', 'LangChain',
];

const SoftwareDevelopmentPage = () => {
  const softwareService = services.find((s) => s.slug === 'software-development');
  const otherServices = services.filter((s) => s.slug !== 'software-development');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Software Development',
    provider: {
      '@type': 'Organization',
      name: companyInfo.name,
      telephone: companyInfo.phone,
      email: companyInfo.email,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Las Vegas',
        addressRegion: 'NV',
        addressCountry: 'US',
      },
    },
    areaServed: 'US',
    description:
      'Custom software development, blockchain-backed platforms, SaaS products and API integrations. End-to-end: from discovery and design to engineering, refinement and delivery.',
  };

  return (
    <div className="min-h-screen bg-black" data-testid="software-development-page">
      <SEO
        title="Software Development Company in Las Vegas | Infinity Solutions Hub"
        description="Custom software development, blockchain real estate platforms, SaaS products and API integrations. We design, develop, refine and deliver tailored software that scales your business. Call 702-605-7282."
        keywords="software development, custom software, blockchain real estate, fractional real estate investing, smart contracts, real estate tokenization, SaaS development, API integration, enterprise software, Las Vegas software company"
        canonical="https://infinitysolutionshub.com/services/software-development"
        jsonLd={jsonLd}
      />

      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        {/* Animated tech-logo background */}
        <TechBackground />

        <div className="container mx-auto px-4 lg:px-8 relative">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-white/60 hover:text-emerald-400 transition-colors mb-8"
            data-testid="back-to-services-link"
          >
            <ArrowLeft size={18} />
            <span className="font-mono text-sm">Back to Services</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex p-4 rounded-xl bg-emerald-500/10 text-emerald-400 mb-6 border border-emerald-500/30">
                <Code size={40} />
              </div>

              <h1 className="hero-heading text-white mb-6" data-testid="software-hero-heading">
                Software built
                <br />
                for the way <span className="text-emerald-400">you work.</span>
              </h1>

              <p className="body-large text-white/70 mb-8">
                We work closely with businesses to design and develop tailored software systems that simplify workflows and support scalable growth. If there are areas where you&apos;re currently navigating operational or digital challenges, we build software that fits, not software that forces you to adapt.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                {softwareService?.features?.map((feature, idx) => (
                  <span
                    key={idx}
                    className="service-button bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                  >
                    {feature}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/#contact"
                  className="cta-button bg-emerald-500 text-black border-emerald-500 hover:bg-emerald-400"
                  data-testid="software-hero-cta-quote"
                >
                  Start Your Project
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="cta-button bg-transparent text-white border-white/30 hover:border-white/60 flex items-center gap-2"
                  data-testid="software-hero-cta-call"
                >
                  <Phone size={16} />
                  Call for 20% OFF
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 backdrop-blur-sm">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles size={18} className="text-emerald-400" />
                  <span className="font-mono text-xs text-emerald-400 tracking-widest">WHY CHOOSE US</span>
                </div>
                <ul className="space-y-4">
                  {softwareService?.benefits?.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Case Study: Blockchain */}
      <section className="py-20 lg:py-28 border-t border-white/10">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-5"
            >
              <span className="service-button bg-emerald-500/10 text-emerald-400 border-emerald-500/30 mb-4 inline-block">
                Featured Build
              </span>
              <h2 className="section-heading text-white mt-4 mb-6">
                Fractional <span className="text-emerald-400">real estate</span> investing,
                <br />
                powered by smart contracts.
              </h2>
              <p className="body-medium text-white/70 mb-6">
                We developed a blockchain-backed real estate platform that lets everyday investors own a piece of real property, fractionally. Each investment is secured by a smart contract on-chain, with compliance, documentation and payouts handled end-to-end so the investor experience stays simple, transparent and trustworthy.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Tokenized real estate ownership, secured by smart contracts',
                  'KYC / AML flows wired into the investor journey',
                  'Transparent on-chain record of holdings and transactions',
                  'Rental income & payouts distributed to investors automatically',
                  'Admin console for sponsors to manage properties end-to-end',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/80">
                    <Check size={18} className="text-emerald-400 flex-shrink-0 mt-1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium"
                data-testid="case-study-cta"
              >
                Build something like this <ArrowRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="lg:col-span-7"
            >
              <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/5 via-black to-cyan-500/5 p-6 lg:p-8 overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
                <div className="relative grid grid-cols-2 gap-4">
                  {[
                    { label: 'Asset Class', value: 'Real Estate', sub: 'tokenized on-chain' },
                    { label: 'Ownership', value: 'Fractional', sub: 'accessible to all' },
                    { label: 'Contracts', value: 'Smart', sub: 'automated & transparent' },
                    { label: 'Architecture', value: 'Secure', sub: 'built to scale' },
                  ].map((stat, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl border border-white/10 bg-white/5 p-5"
                    >
                      <div className="font-mono text-xs text-white/50 tracking-widest mb-2 uppercase">
                        {stat.label}
                      </div>
                      <div className="text-2xl lg:text-3xl text-emerald-400 font-semibold">
                        {stat.value}
                      </div>
                      <div className="text-white/50 text-sm mt-1">{stat.sub}</div>
                    </div>
                  ))}
                </div>

                <div className="relative mt-6 rounded-xl border border-emerald-500/30 bg-black/60 p-5 font-mono text-sm text-emerald-300">
                  <div className="text-white/40 mb-2">// PropertyShares.sol</div>
                  <div className="text-emerald-400">contract</div>{' '}
                  <span className="text-white">PropertyShares</span>{' '}
                  <span className="text-white/60">is</span>{' '}
                  <span className="text-cyan-400">ERC20</span>
                  <span className="text-white">, </span>
                  <span className="text-cyan-400">Ownable</span>
                  <span className="text-white"> {'{'}</span>
                  <div className="pl-4 text-white/70">string public propertyAddress;</div>
                  <div className="pl-4 text-white/70">uint256 public propertyValue;</div>
                  <div className="pl-4 text-white/70">mapping(address =&gt; bool) kycApproved;</div>
                  <div className="text-white">{'}'}</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 lg:py-28 bg-white/[0.02] border-y border-white/10">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-14 max-w-3xl"
          >
            <span className="service-button bg-emerald-500/10 text-emerald-400 border-emerald-500/30 mb-4 inline-block">
              Capabilities
            </span>
            <h2 className="section-heading text-white mt-4">
              Everything you need to ship{' '}
              <span className="text-emerald-400">serious software.</span>
            </h2>
            <p className="body-medium text-white/60 mt-4">
              From the first line of code to the last deploy, we handle every layer of the stack, so you get one team, one plan, one roadmap.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {capabilities.map((cap, idx) => {
              const CapIcon = cap.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="group rounded-xl border border-white/10 bg-white/[0.02] p-6 hover:border-emerald-500/40 hover:bg-emerald-500/[0.04] transition-all"
                  data-testid={`capability-card-${idx}`}
                >
                  <div className="inline-flex p-3 rounded-lg bg-emerald-500/10 text-emerald-400 mb-5 group-hover:scale-110 transition-transform">
                    <CapIcon size={22} />
                  </div>
                  <h3 className="card-heading text-white mb-2">{cap.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{cap.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="service-button bg-emerald-500/10 text-emerald-400 border-emerald-500/30 mb-4 inline-block">
              Our Process
            </span>
            <h2 className="section-heading text-white mt-4">
              From idea to delivery,{' '}
              <span className="text-emerald-400">we handle it all.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
            {process.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="relative"
              >
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 h-full hover:border-emerald-500/40 transition-colors">
                  <div className="font-mono text-xs text-emerald-400 tracking-widest mb-3">
                    {step.step}
                  </div>
                  <h3 className="text-white text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {idx < process.length - 1 && (
                  <div className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 items-center justify-center">
                    <ArrowRight size={16} className="text-white/20" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="lg:w-1/3">
              <div className="flex items-center gap-2 mb-3">
                <Rocket size={18} className="text-emerald-400" />
                <span className="font-mono text-xs text-emerald-400 tracking-widest">TECH WE USE</span>
              </div>
              <h3 className="text-white text-2xl font-semibold">
                A modern stack, chosen with intent.
              </h3>
            </div>
            <div className="lg:w-2/3 flex flex-wrap gap-2">
              {stack.map((tech, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] text-white/70 text-sm font-mono hover:border-emerald-500/40 hover:text-emerald-400 transition-colors"
                  data-testid={`stack-pill-${idx}`}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABanner />

      {/* Other Services */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <h2 className="section-heading text-white mb-12">
            Explore Other <span className="text-emerald-400">Services</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {otherServices.map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                className="group rounded-xl border border-white/10 bg-white/[0.02] p-6 hover:border-emerald-500/40 hover:bg-emerald-500/[0.04] transition-all"
                data-testid={`other-service-${s.slug}`}
              >
                <h3 className="card-heading text-white mb-2 group-hover:underline">
                  {s.title}
                </h3>
                <p className="text-white/60 text-sm">{s.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SoftwareDevelopmentPage;
